import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ToastProvider, useToast } from './hooks/useToast';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ChiamataDetailPage } from './pages/ChiamataDetailPage';
import { ReportPage } from './pages/ReportPage';
import { NuovaChiamataPage } from './pages/NuovaChiamataPage';
import { UtilitaPage } from './pages/UtilitaPage';
import { ImpostazioniPage } from './pages/ImpostazioniPage';
import { SegnalazioniPage } from './pages/SegnalazioniPage';
import { OdlListPage } from './pages/OdlListPage';
import { OdlDetailPage } from './pages/OdlDetailPage';
import { OdlReportPage } from './pages/OdlReportPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useVersionChecker } from './hooks/useVersionChecker';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

function AppShell() {
  useVersionChecker();
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-semaforo-dark-50">
        <div className="w-12 h-12 border-4 border-semaforo-verde-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/chiamata/:id" element={<ChiamataDetailPage />} />
        <Route path="/chiamata/:id/report" element={<ReportPage />} />
        <Route path="/odl" element={<OdlListPage />} />
        <Route path="/odl/:id" element={<OdlDetailPage />} />
        <Route path="/odl/:id/report" element={<OdlReportPage />} />
        <Route path="/nuova" element={<NuovaChiamataPage />} />
        <Route path="/segnalazioni" element={<SegnalazioniPage />} />
        <Route path="/seriale" element={<Navigate to="/utilita" replace />} />
        <Route path="/utilita" element={<UtilitaPage />} />
        <Route path="/impostazioni" element={<ImpostazioniPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ToastContainer() {
  const { toasts, removeToast } = useToast();
  return (
    <div className="fixed bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-80 z-[110] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <button
          key={t.id}
          onClick={() => removeToast(t.id)}
          className={`pointer-events-auto text-left animate-slide-up flex items-start gap-3 p-4 rounded-xl shadow-xl ${
            t.type === 'success' ? 'bg-semaforo-verde-500 text-white' :
            t.type === 'error' ? 'bg-semaforo-rosso-500 text-white' :
            t.type === 'warning' ? 'bg-semaforo-arancio-500 text-white' :
            'bg-semaforo-dark-800 text-white'
          }`}
        >
          <span className="flex-1">{t.message}</span>
          <span className="opacity-80">✕</span>
        </button>
      ))}
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AppShell />
            </ErrorBoundary>
            <ToastContainer />
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}